import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useToast,
} from '@/components/base';

import {
  type Case,
} from '@/hooks/useCases';

import {
  type CaseDeadline,
  useCaseDeadlines,
} from '@/hooks/useCaseDeadlines';

import {
  useLegalDeadlines,
} from '@/hooks/useLegalDeadlines';

import {
  suggestCaseDeadlineDueDate,
} from '@/lib/caseDeadlineSuggestion';

type Props = {
  caseItem:
    Case;

  onChanged?:
    () =>
      void |
      Promise<void>;
};

type DeadlineForm = {
  legalDeadlineId:
    string;

  title:
    string;

  startDate:
    string;

  dueDate:
    string;

  responsibleUser:
    string;

  notes:
    string;

  createTask:
    boolean;
};

type EditDeadlineForm = {
  title:
    string;

  startDate:
    string;

  dueDate:
    string;

  responsibleUser:
    string;

  riskLevel:
    string;

  notes:
    string;
};

const createEmptyForm =
  (
    responsibleUser:
      string |
      null |
      undefined
  ):
    DeadlineForm => ({
    legalDeadlineId:
      '',

    title:
      '',

    startDate:
      '',

    dueDate:
      '',

    responsibleUser:
      responsibleUser ??
      '',

    notes:
      '',

    createTask:
      true,
  });

const createEditForm =
  (
    deadline:
      CaseDeadline
  ):
    EditDeadlineForm => ({
    title:
      deadline.title,

    startDate:
      deadline.startDate ??
      '',

    dueDate:
      deadline.dueDate ??
      '',

    responsibleUser:
      deadline.responsibleUser ??
      '',

    riskLevel:
      deadline.riskLevel,

    notes:
      deadline.notes ??
      '',
  });

const formatDate =
  (
    value:
      string |
      null |
      undefined
  ) => {
    if (!value) {
      return '—';
    }

    const parsed =
      new Date(
        `${value}T12:00:00`
      );

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return value;
    }

    return parsed.toLocaleDateString(
      'pt-BR'
    );
  };

const countTypeLabel =
  (
    value:
      string |
      null
  ) => {
    if (
      value ===
      'business_days'
    ) {
      return 'dias úteis';
    }

    if (
      value ===
      'calendar_days'
    ) {
      return 'dias corridos';
    }

    return 'contagem variável';
  };

const riskLabel =
  (
    value:
      string
  ) => {
    if (
      value ===
      'high'
    ) {
      return 'Alto';
    }

    if (
      value ===
      'low'
    ) {
      return 'Baixo';
    }

    return 'Médio';
  };

const statusLabel =
  (
    value:
      string
  ) => {
    if (
      value ===
      'in_progress'
    ) {
      return 'Em andamento';
    }

    if (
      value ===
      'completed'
    ) {
      return 'Concluído';
    }

    if (
      value ===
      'cancelled'
    ) {
      return 'Cancelado';
    }

    if (
      value ===
      'archived'
    ) {
      return 'Arquivado';
    }

    return 'Pendente';
  };

const countTypeForSuggestion =
  (
    value:
      string |
      null
  ):
    'business_days' |
    'calendar_days' |
    'variable' => {
    if (
      value ===
        'business_days' ||
      value ===
        'calendar_days'
    ) {
      return value;
    }

    return 'variable';
  };

export default function CaseDeadlinesPanel(
  {
    caseItem,
    onChanged,
  }:
    Props
) {
  const {
    addToast,
  } =
    useToast();

  const {
    legalDeadlines,
    isLoadingLegalDeadlines,
    loadLegalDeadlines,
  } =
    useLegalDeadlines({
      autoLoad:
        false,
    });

  const {
    caseDeadlines,
    isLoadingCaseDeadlines,
    isSavingCaseDeadline,
    caseDeadlinesError,

    loadCaseDeadlines,
    createCaseDeadline,
    updateCaseDeadline,
    archiveCaseDeadline,
  } =
    useCaseDeadlines({
      autoLoad:
        false,

      caseId:
        caseItem.id,
    });

  const [
    form,
    setForm,
  ] =
    useState<
      DeadlineForm
    >(
      createEmptyForm(
        caseItem.responsibleUser
      )
    );

  const [
    editingDeadlineId,
    setEditingDeadlineId,
  ] =
    useState(
      ''
    );

  const [
    editForm,
    setEditForm,
  ] =
    useState<
      EditDeadlineForm |
      null
    >(
      null
    );

  useEffect(
    () => {
      setForm(
        createEmptyForm(
          caseItem.responsibleUser
        )
      );

      setEditingDeadlineId(
        ''
      );

      setEditForm(
        null
      );

      void loadLegalDeadlines();

      void loadCaseDeadlines({
        caseId:
          caseItem.id,
      }).catch(
        () =>
          undefined
      );
    },
    [
      caseItem.id,
      caseItem.responsibleUser,
      loadCaseDeadlines,
      loadLegalDeadlines,
    ]
  );

  const selectedRule =
    useMemo(
      () =>
        legalDeadlines.find(
          (
            rule
          ) =>
            rule.id ===
            form.legalDeadlineId
        ) ??
        null,
      [
        form.legalDeadlineId,
        legalDeadlines,
      ]
    );

  const suggestion =
    useMemo(
      () => {
        if (
          !selectedRule
        ) {
          return {
            suggestedDueDate:
              null,

            mode:
              'manual_required' as const,

            explanation:
              'Prazo manual: informe diretamente a data final conferida.',
          };
        }

        return suggestCaseDeadlineDueDate({
          startDate:
            form.startDate,

          daysCount:
            selectedRule.daysCount,

          countType:
            selectedRule.countType,
        });
      },
      [
        form.startDate,
        selectedRule,
      ]
    );

  const afterChanged =
    async () => {
      if (
        onChanged
      ) {
        await onChanged();
      }
    };

  const handleRuleChange =
    (
      legalDeadlineId:
        string
    ) => {
      const rule =
        legalDeadlines.find(
          (
            item
          ) =>
            item.id ===
            legalDeadlineId
        );

      setForm(
        (
          current
        ) => ({
          ...current,

          legalDeadlineId,

          title:
            rule?.name ??
            (
              legalDeadlineId
                ? current.title
                : ''
            ),
        })
      );
    };

  const handleCreate =
    async () => {
      const title =
        form.title.trim() ||
        selectedRule?.name ||
        '';

      if (!title) {
        addToast(
          'Informe o título do prazo ou selecione uma regra jurídica.',
          'warning'
        );

        return;
      }

      if (
        form.createTask &&
        !form.dueDate
      ) {
        addToast(
          'Confirme a data final antes de gerar a tarefa.',
          'warning'
        );

        return;
      }

      try {
        const created =
          await createCaseDeadline({
            caseId:
              caseItem.id,

            legalDeadlineId:
              form.legalDeadlineId ||
              null,

            title,

            startDate:
              form.startDate ||
              null,

            suggestedDueDate:
              suggestion.suggestedDueDate,

            dueDate:
              form.dueDate ||
              null,

            responsibleUser:
              form.responsibleUser ||
              null,

            notes:
              form.notes ||
              null,

            createTask:
              form.createTask,
          });

        await afterChanged();

        addToast(
          created.taskId
            ? 'Prazo criado e tarefa vinculada com sucesso.'
            : 'Prazo operacional criado com sucesso.',
          'success'
        );

        setForm(
          createEmptyForm(
            caseItem.responsibleUser
          )
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível criar o prazo operacional.',
          'error'
        );
      }
    };

  const openEdit =
    (
      deadline:
        CaseDeadline
    ) => {
      setEditingDeadlineId(
        deadline.id
      );

      setEditForm(
        createEditForm(
          deadline
        )
      );
    };

  const closeEdit =
    () => {
      setEditingDeadlineId(
        ''
      );

      setEditForm(
        null
      );
    };

  const handleSaveEdit =
    async (
      deadline:
        CaseDeadline
    ) => {
      if (
        !editForm ||
        editingDeadlineId !==
        deadline.id
      ) {
        return;
      }

      if (
        !editForm.title.trim()
      ) {
        addToast(
          'Informe o título do prazo.',
          'warning'
        );

        return;
      }

      let nextSuggestedDueDate =
        deadline.suggestedDueDate;

      const startChanged =
        editForm.startDate !==
        (
          deadline.startDate ??
          ''
        );

      if (
        startChanged
      ) {
        const recalculated =
          suggestCaseDeadlineDueDate({
            startDate:
              editForm.startDate,

            daysCount:
              deadline.daysCountSnapshot,

            countType:
              countTypeForSuggestion(
                deadline.countTypeSnapshot
              ),
          });

        nextSuggestedDueDate =
          recalculated.suggestedDueDate;
      }

      try {
        await updateCaseDeadline(
          deadline.id,
          {
            title:
              editForm.title.trim(),

            startDate:
              editForm.startDate ||
              null,

            suggestedDueDate:
              nextSuggestedDueDate,

            dueDate:
              editForm.dueDate ||
              null,

            responsibleUser:
              editForm.responsibleUser ||
              null,

            riskLevel:
              editForm.riskLevel,

            notes:
              editForm.notes ||
              null,
          }
        );

        await afterChanged();

        closeEdit();

        addToast(
          deadline.taskId
            ? 'Prazo e tarefa vinculada atualizados.'
            : 'Prazo atualizado com sucesso.',
          'success'
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível atualizar o prazo.',
          'error'
        );
      }
    };

  const handleStatus =
    async (
      deadline:
        CaseDeadline,

      status:
        'pending' |
        'in_progress' |
        'completed' |
        'cancelled'
    ) => {
      try {
        await updateCaseDeadline(
          deadline.id,
          {
            status,
          }
        );

        await afterChanged();

        addToast(
          status ===
          'completed'
            ? 'Prazo concluído com sucesso.'
            : status ===
              'cancelled'
              ? 'Prazo cancelado.'
              : 'Status do prazo atualizado.',
          'success'
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível atualizar o status.',
          'error'
        );
      }
    };

  const handleCreateTask =
    async (
      deadline:
        CaseDeadline
    ) => {
      if (
        !deadline.dueDate
      ) {
        addToast(
          'Confirme a data final antes de gerar a tarefa.',
          'warning'
        );

        return;
      }

      try {
        await updateCaseDeadline(
          deadline.id,
          {
            createTask:
              true,
          }
        );

        addToast(
          'Tarefa criada e vinculada ao prazo.',
          'success'
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível gerar a tarefa.',
          'error'
        );
      }
    };

  const handleArchive =
    async (
      deadline:
        CaseDeadline
    ) => {
      const confirmed =
        window.confirm(
          'Arquivar este prazo operacional? A tarefa vinculada também será arquivada.'
        );

      if (!confirmed) {
        return;
      }

      try {
        await archiveCaseDeadline(
          deadline.id
        );

        await afterChanged();

        addToast(
          'Prazo arquivado com sucesso.',
          'success'
        );
      } catch (
        error
      ) {
        addToast(
          error instanceof Error
            ? error.message
            : 'Não foi possível arquivar o prazo.',
          'error'
        );
      }
    };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-background-200 bg-background-50 p-4 space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-foreground-800">
            Novo prazo operacional
          </h4>

          <p className="text-xs text-foreground-500 mt-1">
            A sugestão do sistema não substitui a conferência jurídica da data final.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1">
            Regra jurídica
          </label>

          <select
            value={form.legalDeadlineId}
            onChange={(event) =>
              handleRuleChange(
                event.target.value
              )
            }
            disabled={isLoadingLegalDeadlines}
            className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer disabled:opacity-60"
          >
            <option value="">
              Prazo manual / sem regra vinculada
            </option>

            {legalDeadlines.map(
              (
                rule
              ) => (
                <option
                  key={rule.id}
                  value={rule.id}
                >
                  {rule.legalArea} — {rule.name}
                </option>
              )
            )}
          </select>
        </div>

        {selectedRule && (
          <div className="rounded-lg border border-primary-100 bg-primary-50/50 p-3 space-y-1">
            <p className="text-xs font-semibold text-primary-800">
              {selectedRule.name}
            </p>

            <p className="text-xs text-foreground-600">
              {selectedRule.daysCount === null
                ? 'Quantidade variável'
                : `${selectedRule.daysCount} ${countTypeLabel(
                    selectedRule.countType
                  )}`}
              {' · '}
              Risco: {riskLabel(
                selectedRule.riskLevel
              )}
            </p>

            {selectedRule.legalBasis && (
              <p className="text-xs text-foreground-500">
                {selectedRule.legalBasis}
              </p>
            )}

            {selectedRule.startTerm && (
              <p className="text-xs text-foreground-500">
                Termo inicial: {selectedRule.startTerm}
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1">
            Título
          </label>

          <input
            type="text"
            value={form.title}
            onChange={(event) =>
              setForm(
                (
                  current
                ) => ({
                  ...current,

                  title:
                    event.target.value,
                })
              )
            }
            className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            placeholder="Ex.: Apresentar contestação"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">
              Marco inicial
            </label>

            <input
              type="date"
              value={form.startDate}
              onChange={(event) =>
                setForm(
                  (
                    current
                  ) => ({
                    ...current,

                    startDate:
                      event.target.value,
                  })
                )
              }
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">
              Data final conferida
            </label>

            <input
              type="date"
              value={form.dueDate}
              onChange={(event) =>
                setForm(
                  (
                    current
                  ) => ({
                    ...current,

                    dueDate:
                      event.target.value,
                  })
                )
              }
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            />
          </div>
        </div>

        <div
          className={`rounded-lg border p-3 ${
            suggestion.mode ===
            'automatic'
              ? 'border-emerald-200 bg-emerald-50'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          <p className="text-xs font-semibold text-foreground-700">
            Sugestão do sistema
          </p>

          {suggestion.suggestedDueDate ? (
            <div className="flex items-center justify-between gap-3 mt-1">
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  {formatDate(
                    suggestion.suggestedDueDate
                  )}
                </p>

                <p className="text-[11px] text-foreground-500 mt-0.5">
                  {suggestion.explanation}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setForm(
                    (
                      current
                    ) => ({
                      ...current,

                      dueDate:
                        suggestion.suggestedDueDate ??
                        current.dueDate,
                    })
                  )
                }
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Usar sugestão
              </button>
            </div>
          ) : (
            <p className="text-xs text-foreground-600 mt-1">
              {suggestion.explanation}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1">
            Responsável
          </label>

          <input
            type="text"
            value={form.responsibleUser}
            onChange={(event) =>
              setForm(
                (
                  current
                ) => ({
                  ...current,

                  responsibleUser:
                    event.target.value,
                })
              )
            }
            className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
            placeholder="Responsável pelo prazo"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1">
            Observações
          </label>

          <textarea
            value={form.notes}
            onChange={(event) =>
              setForm(
                (
                  current
                ) => ({
                  ...current,

                  notes:
                    event.target.value,
                })
              )
            }
            rows={3}
            className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
          />
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.createTask}
            onChange={(event) =>
              setForm(
                (
                  current
                ) => ({
                  ...current,

                  createTask:
                    event.target.checked,
                })
              )
            }
            className="mt-0.5"
          />

          <span>
            <span className="block text-xs font-medium text-foreground-700">
              Gerar tarefa automaticamente
            </span>

            <span className="block text-[11px] text-foreground-500 mt-0.5">
              A tarefa será uma projeção operacional deste prazo e permanecerá sincronizada.
            </span>
          </span>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() =>
              void handleCreate()
            }
            disabled={isSavingCaseDeadline}
            className="px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSavingCaseDeadline
              ? 'Salvando...'
              : 'Criar Prazo'}
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-foreground-800">
            Prazos do caso
          </h4>

          <span className="text-xs text-foreground-400">
            {caseDeadlines.length} registro(s)
          </span>
        </div>

        {isLoadingCaseDeadlines && (
          <div className="rounded-lg border border-dashed border-background-200 p-3 text-xs text-foreground-500">
            Carregando prazos...
          </div>
        )}

        {caseDeadlinesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            {caseDeadlinesError}
          </div>
        )}

        {!isLoadingCaseDeadlines &&
          !caseDeadlinesError &&
          caseDeadlines.length ===
            0 && (
            <div className="rounded-lg border border-dashed border-background-200 p-4 text-center">
              <p className="text-sm text-foreground-600">
                Nenhum prazo operacional registrado neste caso.
              </p>
            </div>
          )}

        <div className="space-y-3">
          {caseDeadlines.map(
            (
              deadline
            ) => {
              const isEditing =
                editingDeadlineId ===
                  deadline.id &&
                Boolean(
                  editForm
                );

              return (
                <div
                  key={deadline.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-3"
                >
                  {isEditing &&
                  editForm ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Título
                        </label>

                        <input
                          value={editForm.title}
                          onChange={(event) =>
                            setEditForm(
                              {
                                ...editForm,

                                title:
                                  event.target.value,
                              }
                            )
                          }
                          className="w-full px-3 py-2 border border-background-200 rounded-lg text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Marco inicial
                          </label>

                          <input
                            type="date"
                            value={editForm.startDate}
                            onChange={(event) =>
                              setEditForm(
                                {
                                  ...editForm,

                                  startDate:
                                    event.target.value,
                                }
                              )
                            }
                            className="w-full px-3 py-2 border border-background-200 rounded-lg text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Data final conferida
                          </label>

                          <input
                            type="date"
                            value={editForm.dueDate}
                            onChange={(event) =>
                              setEditForm(
                                {
                                  ...editForm,

                                  dueDate:
                                    event.target.value,
                                }
                              )
                            }
                            className="w-full px-3 py-2 border border-background-200 rounded-lg text-sm"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Responsável
                          </label>

                          <input
                            value={editForm.responsibleUser}
                            onChange={(event) =>
                              setEditForm(
                                {
                                  ...editForm,

                                  responsibleUser:
                                    event.target.value,
                                }
                              )
                            }
                            className="w-full px-3 py-2 border border-background-200 rounded-lg text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Risco
                          </label>

                          <select
                            value={editForm.riskLevel}
                            onChange={(event) =>
                              setEditForm(
                                {
                                  ...editForm,

                                  riskLevel:
                                    event.target.value,
                                }
                              )
                            }
                            className="w-full px-3 py-2 border border-background-200 rounded-lg text-sm bg-background-50"
                          >
                            <option value="low">
                              Baixo
                            </option>

                            <option value="medium">
                              Médio
                            </option>

                            <option value="high">
                              Alto
                            </option>
                          </select>
                        </div>
                      </div>

                      <textarea
                        value={editForm.notes}
                        onChange={(event) =>
                          setEditForm(
                            {
                              ...editForm,

                              notes:
                                event.target.value,
                            }
                          )
                        }
                        rows={2}
                        className="w-full px-3 py-2 border border-background-200 rounded-lg text-sm resize-none"
                        placeholder="Observações"
                      />

                      <div className="flex justify-end gap-2">
                        <button
                          onClick={closeEdit}
                          disabled={isSavingCaseDeadline}
                          className="px-2.5 py-1.5 rounded-lg text-xs border border-background-200"
                        >
                          Cancelar
                        </button>

                        <button
                          onClick={() =>
                            void handleSaveEdit(
                              deadline
                            )
                          }
                          disabled={isSavingCaseDeadline}
                          className="px-2.5 py-1.5 rounded-lg text-xs bg-primary-500 text-background-50"
                        >
                          Salvar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-foreground-800">
                            {deadline.title}
                          </p>

                          <p className="text-xs text-foreground-500 mt-0.5">
                            {deadline.legalDeadlineName ||
                              deadline.ruleNameSnapshot ||
                              'Prazo manual'}
                          </p>
                        </div>

                        <span className="text-[11px] font-medium px-2 py-1 rounded-md bg-background-100 text-foreground-600 whitespace-nowrap">
                          {statusLabel(
                            deadline.status
                          )}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                            Marco inicial
                          </p>

                          <p className="text-xs text-foreground-700 mt-0.5">
                            {formatDate(
                              deadline.startDate
                            )}
                          </p>
                        </div>

                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                            Data conferida
                          </p>

                          <p className="text-xs font-semibold text-foreground-800 mt-0.5">
                            {formatDate(
                              deadline.dueDate
                            )}
                          </p>
                        </div>

                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                            Sugestão
                          </p>

                          <p className="text-xs text-foreground-700 mt-0.5">
                            {formatDate(
                              deadline.suggestedDueDate
                            )}
                          </p>
                        </div>

                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-foreground-400">
                            Responsável
                          </p>

                          <p className="text-xs text-foreground-700 mt-0.5">
                            {deadline.responsibleUser ||
                              '—'}
                          </p>
                        </div>
                      </div>

                      {deadline.legalBasisSnapshot && (
                        <p className="text-xs text-foreground-500 mt-3">
                          {deadline.legalBasisSnapshot}
                        </p>
                      )}

                      {deadline.taskId && (
                        <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700">
                          <i className="ri-checkbox-circle-line"></i>

                          <span>
                            Tarefa sincronizada
                            {deadline.taskTitle
                              ? `: ${deadline.taskTitle}`
                              : ''}
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-background-100">
                        <button
                          onClick={() =>
                            openEdit(
                              deadline
                            )
                          }
                          disabled={isSavingCaseDeadline}
                          className="px-2 py-1 rounded-md text-[11px] border border-background-200 text-foreground-600"
                        >
                          Editar
                        </button>

                        {deadline.status !==
                          'completed' &&
                          deadline.status !==
                            'cancelled' && (
                            <>
                              <button
                                onClick={() =>
                                  void handleStatus(
                                    deadline,
                                    'completed'
                                  )
                                }
                                disabled={isSavingCaseDeadline}
                                className="px-2 py-1 rounded-md text-[11px] border border-emerald-200 text-emerald-700"
                              >
                                Concluir
                              </button>

                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      'Cancelar este prazo?'
                                    )
                                  ) {
                                    void handleStatus(
                                      deadline,
                                      'cancelled'
                                    );
                                  }
                                }}
                                disabled={isSavingCaseDeadline}
                                className="px-2 py-1 rounded-md text-[11px] border border-amber-200 text-amber-700"
                              >
                                Cancelar prazo
                              </button>
                            </>
                          )}

                        {(deadline.status ===
                          'completed' ||
                          deadline.status ===
                            'cancelled') && (
                          <button
                            onClick={() =>
                              void handleStatus(
                                deadline,
                                'pending'
                              )
                            }
                            disabled={isSavingCaseDeadline}
                            className="px-2 py-1 rounded-md text-[11px] border border-background-200 text-foreground-600"
                          >
                            Reabrir
                          </button>
                        )}

                        {!deadline.taskId && (
                          <button
                            onClick={() =>
                              void handleCreateTask(
                                deadline
                              )
                            }
                            disabled={
                              isSavingCaseDeadline ||
                              !deadline.dueDate
                            }
                            className="px-2 py-1 rounded-md text-[11px] border border-primary-200 text-primary-700 disabled:opacity-50"
                          >
                            Gerar tarefa
                          </button>
                        )}

                        <button
                          onClick={() =>
                            void handleArchive(
                              deadline
                            )
                          }
                          disabled={isSavingCaseDeadline}
                          className="px-2 py-1 rounded-md text-[11px] border border-red-200 text-red-700"
                        >
                          Arquivar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}